import { Play, RotateCcw } from 'lucide-react'
import { useState } from 'react'

import { QuoteCard } from '@/components/quotes/QuoteCard'
import { QuoteCardSkeleton } from '@/components/quotes/QuoteCardSkeleton'
import type { QuoteCardData } from '@/components/quotes/quoteCardTypes'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface QuoteCardTransitionDemoProps {
  /** The card the skeleton resolves into. */
  quote: QuoteCardData
  label: string
}

/**
 * Plays the skeleton → resolved handoff on demand, so the transition can be
 * reviewed on its own rather than caught by chance on a real page load.
 */
export function QuoteCardTransitionDemo({ quote, label }: QuoteCardTransitionDemoProps) {
  const [resolved, setResolved] = useState(false)

  return (
    <div className="relative">
      {/* One grid cell for both layers, so the container holds the taller card's height. */}
      <div className="grid">
        <div
          aria-hidden={resolved}
          inert={resolved}
          className={cn(
            'col-start-1 row-start-1 transition-opacity duration-500',
            resolved && 'pointer-events-none opacity-0'
          )}
        >
          <QuoteCardSkeleton />
        </div>
        <div
          aria-hidden={!resolved}
          inert={!resolved}
          className={cn(
            'col-start-1 row-start-1 transition-opacity duration-500',
            !resolved && 'pointer-events-none opacity-0'
          )}
        >
          <QuoteCard quote={quote} />
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 lg:absolute lg:top-3 lg:left-full lg:mt-0 lg:ml-3">
        <span className="border-ink-200 text-ink-400 bg-ink-50 rounded-full border px-2 py-0.5 font-mono text-[10px] whitespace-nowrap">
          {label}
        </span>
        <Button
          size="icon-sm"
          variant="outline"
          aria-label={resolved ? 'Replay transition' : 'Play transition'}
          onClick={() => setResolved((current) => !current)}
        >
          {resolved ? <RotateCcw /> : <Play />}
        </Button>
      </div>
    </div>
  )
}
